import React, { useContext } from 'react';
import classNames from 'classnames';
import { NdaStatus } from 'models/Nda';
import { useTranslation } from 'react-i18next';
import useNdaStatus, { LeadNdaProps } from 'hooks/useNdaStatus';
import {
  dateFormat,
  isNdaCounterSignerPending,
  isNdaNotSent,
  isNdaRejected,
  isNdaSent,
} from 'utils';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import { Button, Loader } from 'ncoded-component-library';
import VerifiedBadge from 'components/VerifiedBadge';
import LeadContext from '../../subrouters/Portfolio/subrouters/Buyer/providers/Lead/Lead.context';

import './BuyerNdaCard.styles.scss';

type BuyerNdaCardProps = {
  className?: string;
  leadId: string;
  signingStatus: NdaStatus;
  envelopeId: string;
  signedDate: Date;
  rejectionReason?: string;
  manuallySetNDAStatus: boolean;
  envelopeTemplateName: string;
  updateNdaProps: (leadNdaProps: LeadNdaProps) => void;
};

const BuyerNdaCard: React.FC<BuyerNdaCardProps> = (props) => {
  const {
    className,
    leadId,
    signingStatus,
    envelopeId,
    signedDate,
    rejectionReason,
    manuallySetNDAStatus,
    envelopeTemplateName,
    updateNdaProps,
  } = props;

  const { isWaitingForWebhook } = useContext(LeadContext);

  const classes = classNames('buyer-nda-card', className);

  const { t } = useTranslation();

  const { isLoading, getAndOpenNdaLink } = useNdaStatus(
    leadId,
    envelopeId,
    updateNdaProps,
  );

  if (isNdaNotSent(signingStatus)) return null;

  if (isNdaRejected(signingStatus))
    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          {t('ndaRejected')}
        </p>

        <div className="container">
          <p className="nda-label">
            {t('userRejectedNDA', {
              name: t('you'),
              rejectionReason: rejectionReason || '/',
            })}
          </p>
        </div>
      </div>
    );

  if (isNdaCounterSignerPending(signingStatus))
    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          {t('waitingNdaCounter')}
        </p>

        <div className="container">
          <p className="nda-label">{t('youSignedNDAWaitingCoutnersign')}</p>
        </div>
      </div>
    );

  if (isNdaSent(signingStatus)) {
    if (isWaitingForWebhook) {
      return (
        <div className={classes}>
          <p className="heading">{t('ndaProcessing')}</p>

          <div className="container">
            <p className="nda-label">{t('ndaIsBeingProcessing')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          {t('actionRequired')}
        </p>

        <div className="container">
          <p className="nda-label">{t('ndaIsReadyToSign')}</p>

          <Button
            variant="outline"
            disabled={isLoading}
            onClick={getAndOpenNdaLink}
          >
            {isLoading && <Loader />}
            {t('signNDA')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className="container">
        <div className="document">
          <VerifiedBadge />
          <div>
            <p>{envelopeTemplateName || t('nonDisclosureAgreement')}</p>
            <p className="document-date">{dateFormat(signedDate)}</p>
          </div>
        </div>

        {!manuallySetNDAStatus && (
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={getAndOpenNdaLink}
          >
            {isLoading && <Loader />}
            {t('viewSignedNDA')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BuyerNdaCard;
